<template>
    <PanelPage>
        <template v-slot:title> 🧞 {{ $t('messages.genie') }} - {{ selectedTotal }} / {{ total }} </template>

        <div>
            <apexchart type="bar" height="350" :series="chartData.series" :options="chartData"> </apexchart>

            <div>
                <span
                    v-for="filter in selectedFilters"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                >
                    {{ filter.label }}: {{ filter.value }}
                </span>
            </div>

            <div>
                <span
                    class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800"
                    :title="average.toFixed(2)"
                >
                    {{ $t('messages.average') }}:{{ (averagePercent * 100).toFixed(2) }}%
                </span>
            </div>
        </div>

        <div class="">
            {{ this.form }}
        </div>

        <form @submit.prevent="submit">
            <div class="w-full flex space-x-4 justify-end">
                <div class="mt-1">
                    <input
                        id="show_school_government_id"
                        v-model="form.show_school_government_id"
                        name="show_school_government_id"
                        type="checkbox"
                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <label for="show_school_government_id" class="ml-1">{{
                        $t('messages.school_government_id')
                    }}</label>
                </div>

                <div class="mt-1">
                    <SubmitFormButton :button-text="$t('messages.filter')" :processing="form.processing" is-filter />
                </div>

                <div class="mt-1">
                    <AppButton element="button" type="button" @click="list">
                        <svg
                            class="-ml-0.5 mr-2 w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                            ></path>
                        </svg>
                        {{ $t('messages.list') }}
                    </AppButton>
                </div>

                <div class="mt-1" v-if="filters.hasOwnProperty('list')">
                    <AppButton href="#list">
                        <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            ></path>
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            ></path>
                        </svg>
                    </AppButton>
                </div>

                <div class="mt-1">
                    <AppButton type="button" element="button" @click="clear">
                        <svg
                            class="-ml-0.5 mr-2 w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            ></path>
                        </svg>
                        {{ $t('messages.clear_form') }}
                    </AppButton>
                </div>

                <div class="mt-1">
                    <AppButton type="button" element="button" @click="slideOverOpen = true">
                        {{ $t('messages.denominator') }}
                        <span
                            v-if="form.denominator"
                            class="m-1 inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                        ></span>
                        <SlideOver :show="slideOverOpen" @close="slideOverOpen = false">
                            <div>
                                {{ $t('messages.denominator') }}
                            </div>
                            <select
                                name="denominator"
                                id="denominator"
                                v-model="form.denominator"
                                class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                                <option value=""></option>
                                <option v-if="form.study_variable" value="study_variable">
                                    {{ $t('messages.study_variable') }}
                                </option>
                                <option v-if="form.grade" value="grade">
                                    {{ $t('messages.score') }}
                                </option>
                            </select>
                        </SlideOver>
                    </AppButton>
                </div>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-6">
                    <div class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.group') }}
                    </div>
                    <div class="mt-1">
                        <div class="inline-flex items-center" v-for="(groupCheckbox, index) in groupsForCheckboxes">
                            <label class="ml-1 mr-3">
                                <input
                                    name="groups[]"
                                    type="checkbox"
                                    :value="groupCheckbox.id"
                                    v-model="form.groups"
                                    @click="toggleGroup($event, index)"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                    {{ groupCheckbox.name }}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.gender') }}
                        <span
                            v-if="form.gender"
                            class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                        ></span>
                    </div>
                    <div class="mt-1">
                        <div class="inline-flex items-center">
                            <input
                                v-model="form.gender"
                                id="male"
                                name="gender"
                                type="radio"
                                value="male"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label for="male" class="ml-1 mr-3">
                                <span class="block text-sm leading-5 font-medium text-gray-700">{{
                                    $t('messages.male')
                                }}</span>
                            </label>
                        </div>
                        <div class="inline-flex items-center">
                            <input
                                v-model="form.gender"
                                id="female"
                                name="gender"
                                type="radio"
                                value="female"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label for="female" class="ml-1 mr-3">
                                <span class="block text-sm leading-5 font-medium text-gray-700">{{
                                    $t('messages.female')
                                }}</span>
                            </label>
                        </div>
                        <div class="inline-flex items-center">
                            <input
                                v-model="form.gender"
                                id="gender_all"
                                name="gender"
                                type="radio"
                                value=""
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label for="gender_all" class="ml-1 mr-3">
                                <span class="block text-sm leading-5 font-medium text-gray-700">{{
                                    $t('messages.all')
                                }}</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.score') }}
                        <span
                            v-if="form.grade"
                            class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                        ></span>
                    </div>
                    <div class="mt-1">
                        <div class="inline-flex items-center">
                            <input
                                v-model="form.grade"
                                id="grade-all"
                                name="grade"
                                type="radio"
                                value=""
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label for="grade-all" class="ml-1 mr-3">
                                <span class="block text-sm leading-5 font-medium text-gray-700">{{
                                    $t('messages.all')
                                }}</span>
                            </label>
                        </div>
                        <div class="inline-flex items-center">
                            <input
                                v-model="form.grade"
                                id="grade-passing"
                                name="grade"
                                type="radio"
                                value="5"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label for="grade-passing" class="ml-1 mr-3">
                                <span class="block text-sm leading-5 font-medium text-gray-700">{{
                                    $t('messages.activity_passed')
                                }}</span>
                            </label>
                        </div>
                        <div class="inline-flex items-center">
                            <input
                                v-model="form.grade"
                                id="grade-above-passing"
                                name="grade"
                                type="radio"
                                value="7"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label for="grade-above-passing" class="ml-1 mr-3">
                                <span class="block text-sm leading-5 font-medium text-gray-700">AN, AE</span>
                            </label>
                        </div>
                        <div class="inline-flex items-center">
                            <input
                                v-model="form.grade"
                                id="grade-excellent"
                                name="grade"
                                type="radio"
                                value="9"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label for="grade-excellent" class="ml-1 mr-3">
                                <span class="block text-sm leading-5 font-medium text-gray-700">AE</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-1">
                    <div>
                        <label for="absenteeism" class="block text-sm font-medium text-gray-700">
                            {{ $t('messages.absenteeism') }} (>%)
                            <span
                                v-if="form.absenteeism"
                                class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                            ></span>
                        </label>
                        <div class="mt-1">
                            <input
                                type="number"
                                name="absenteeism"
                                id="absenteeism"
                                v-model="form.absenteeism"
                                min="1"
                                max="99"
                                step="1"
                                class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        {{ $t('messages.general_information') }}
                    </h3>
                    <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                        <div class="sm:col-span-2">
                            <label for="study_variable" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.study_variable') }}
                                <span
                                    v-if="form.study_variable"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1 rounded-md shadow-sm">
                                <input
                                    v-model="form.study_variable"
                                    id="study_variable"
                                    name="study_variable"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="has_internet_access" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.has_internet_access') }}
                                <span
                                    v-if="form.has_internet_access"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="has_internet_access"
                                    id="has_internet_access"
                                    v-model="form.has_internet_access"
                                    :missing="true"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="has_device_home" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.has_device_home') }}
                                <span
                                    v-if="form.has_device_home"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="has_device_home"
                                    v-model="form.has_device_home"
                                    id="has_device_home"
                                    :missing="true"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="lunch" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.lunch') }}
                                <span
                                    v-if="form.lunch"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1 rounded-md shadow-sm">
                                <input
                                    id="lunch"
                                    name="lunch"
                                    type="checkbox"
                                    v-model="form.lunch"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        {{ $t('messages.interventions') }}
                    </h3>
                    <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                        <div class="sm:col-span-2">
                            <label for="interventions" class="block text-sm font-medium leading-5 text-gray-700">
                                Servicios sociales
                                <span
                                    v-if="form.interventions.includes('Servicios sociales')"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <input
                                    id="interventions"
                                    name="interventions[]"
                                    value="Servicios sociales"
                                    type="checkbox"
                                    v-model="form.interventions"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="EAP" class="block text-sm font-medium leading-5 text-gray-700">
                                EAP
                                <span
                                    v-if="form.interventions.includes('EAP')"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <input
                                    id="EAP"
                                    name="interventions[]"
                                    value="EAP"
                                    type="checkbox"
                                    v-model="form.interventions"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        {{ $t('messages.attention_to_diversity') }}
                    </h3>
                    <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                        <div class="sm:col-span-2">
                            <label
                                for="disadvantaged_social_situation"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.disadvantaged_social_situation') }}
                                <span
                                    v-if="form.disadvantaged_social_situation"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="disadvantaged_social_situation"
                                    id="disadvantaged_social_situation"
                                    v-model="form.disadvantaged_social_situation"
                                    missing="true"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="social_services" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.social_services') }}
                                <span
                                    v-if="form.social_services"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="social_services"
                                    id="social_services"
                                    v-model="form.social_services"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label
                                for="intensive_support_inclusive_schooling"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.intensive_support_inclusive_schooling') }}
                                <span
                                    v-if="form.intensive_support_inclusive_schooling"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="intensive_support_inclusive_schooling"
                                    v-model="form.intensive_support_inclusive_schooling"
                                    id="intensive_support_inclusive_schooling"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label
                                for="personalized_school_support"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.personalized_school_support') }}
                                <span
                                    v-if="form.personalized_school_support"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="personalized_school_support"
                                    id="personalized_school_support"
                                    v-model="form.personalized_school_support"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label
                                for="school_support_reinforcement"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.school_support_reinforcement') }}
                                <span
                                    v-if="form.school_support_reinforcement"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="school_support_reinforcement"
                                    id="school_support_reinforcement"
                                    v-model="form.school_support_reinforcement"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label
                                for="school_support_special_education"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.school_support_special_education') }}
                                <span
                                    v-if="form.school_support_special_education"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="school_support_special_education"
                                    id="school_support_special_education"
                                    v-model="form.school_support_special_education"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="language_help_class" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.language_help_class') }}
                                <span
                                    v-if="form.language_help_class"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="language_help_class"
                                    id="language_help_class"
                                    v-model="form.language_help_class"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        {{ $tc('messages.authorizations', 2) }}
                    </h3>
                    <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                        <div class="sm:col-span-2">
                            <label
                                for="app_data_authorization"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.app_data_authorization') }}
                                <span
                                    v-if="form.app_data_authorization"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="app_data_authorization"
                                    id="app_data_authorization"
                                    v-model="form.app_data_authorization"
                                    missing="true"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="has_internet_access" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.can_leave_by_themselves') }}
                                <span
                                    v-if="form.can_leave_by_themselves"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="can_leave_by_themselves"
                                    id="can_leave_by_themselves"
                                    v-model="form.can_leave_by_themselves"
                                    missing="true"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="outings" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.outings') }}
                                <span
                                    v-if="form.outings"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <select
                                    name="outings"
                                    id="outings"
                                    v-model="form.outings"
                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                    <option value=""></option>
                                    <option value="0">
                                        {{ $t('messages.no') }}
                                    </option>
                                    <option value="1">
                                        {{ $t('messages.all') }}
                                    </option>
                                    <option value="2">
                                        {{ $t('messages.free') }}
                                    </option>
                                    <option value="NULL">
                                        {{ $t('messages.missing') }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label for="image_rights" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.image_rights') }}
                                <span
                                    v-if="form.image_rights"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <select
                                    name="image_rights"
                                    id="image_rights"
                                    v-model="form.image_rights"
                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                    <option value=""></option>
                                    <option value="0">
                                        {{ $t('messages.no') }}
                                    </option>
                                    <option value="1">
                                        {{ $t('messages.yes') }}
                                    </option>
                                    <option value="2">
                                        {{ $t('messages.other') }}
                                    </option>
                                    <option value="NULL">
                                        {{ $t('messages.missing') }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        {{ $t('messages.scholarships') }}
                    </h3>
                    <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                        <div class="sm:col-span-2">
                            <label
                                for="scholarship_special_education"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.scholarship_special_education') }}
                                <span
                                    v-if="form.scholarship_special_education"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="scholarship_special_education"
                                    id="scholarship_special_education"
                                    v-model="form.scholarship_special_education"
                                />
                            </div>
                        </div>
                        <div class="sm:col-span-2">
                            <label for="scholarship_lunch" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.scholarship_lunch') }}
                                <span
                                    v-if="form.scholarship_lunch"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="scholarship_lunch"
                                    id="scholarship_lunch"
                                    v-model="form.scholarship_lunch"
                                />
                            </div>
                        </div>
                        <div class="sm:col-span-2">
                            <label
                                for="scholarship_school_material"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.scholarship_school_material') }}
                                <span
                                    v-if="form.scholarship_school_material"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="scholarship_school_material"
                                    id="scholarship_school_material"
                                    v-model="form.scholarship_school_material"
                                />
                            </div>
                        </div>
                        <div class="sm:col-span-2">
                            <label
                                for="scholarship_school_trips"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.scholarship_school_trips') }}
                                <span
                                    v-if="form.scholarship_school_trips"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="scholarship_school_trips"
                                    id="scholarship_school_trips"
                                    v-model="form.scholarship_school_trips"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        {{ $t('messages.covid19') }}
                    </h3>
                    <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                        <div class="sm:col-span-2">
                            <label
                                for="statement_of_responsibility"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                {{ $t('messages.statement_of_responsibility') }}
                                <span
                                    v-if="form.statement_of_responsibility"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="statement_of_responsibility"
                                    id="statement_of_responsibility"
                                    v-model="form.statement_of_responsibility"
                                    missing="true"
                                />
                            </div>
                        </div>
                        <div class="sm:col-span-2">
                            <label for="pcr_authorization" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.pcr_authorization') }}
                                <span
                                    v-if="form.pcr_authorization"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="pcr_authorization"
                                    id="pcr_authorization"
                                    v-model="form.pcr_authorization"
                                    missing="true"
                                >
                                </IndeterminateCheckboxSelect>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        {{ $t('messages.family') }}
                    </h3>
                    <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                        <div class="sm:col-span-2">
                            <label for="individual_reports" class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.individual_reports') }}
                                <span
                                    v-if="form.individual_reports"
                                    class="inline-block shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"
                                ></span>
                            </label>
                            <div class="mt-1">
                                <IndeterminateCheckboxSelect
                                    name="individual_reports"
                                    id="individual_reports"
                                    v-model="form.individual_reports"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <div v-for="category in tagGroups" class="mt-5 md:grid md:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ category.name }}
                        </h3>
                        <p class="mt-1 text-sm leading-5 text-gray-500">
                            &nbsp;
                            <input
                                :value="category.category"
                                v-model="form.has_tag_of_category"
                                name="has_tag_of_category"
                                type="radio"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                        </p>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                            <div class="mt-5 md:mt-0 md:col-span-8">
                                <div class="">
                                    <div class="inline-block">
                                        <span
                                            v-for="tag in category.tags"
                                            @click="toggleTag(tag.id)"
                                            :class="
                                                filteredForm.tags.includes(tag.id)
                                                    ? 'bg-indigo-100 text-indigo-800'
                                                    : 'bg-gray-100 text-gray-600'
                                            "
                                            class="cursor-pointer mr-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                        >
                                            <span>
                                                <span v-if="tag.description === null">
                                                    {{ tag.display_name }}
                                                </span>
                                                <abbr v-else :title="tag.description">
                                                    {{ tag.display_name }}
                                                </abbr>
                                                <input
                                                    type="checkbox"
                                                    class="hidden"
                                                    v-model="form.tags"
                                                    name="tags"
                                                    :value="tag.id"
                                                />
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <hr class="my-5" />

        <div class="flex flex-col mt-5" id="list" v-if="students.length">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="w-1 whitespace-nowrap px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.group') }}
                                </th>
                                <th
                                    v-if="filters.hasOwnProperty('show_school_government_id')"
                                    class="w-1 whitespace-nowrap px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.school_government_id') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(student, index) in students"
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ student.group.name }}
                                </td>
                                <td
                                    v-if="filters.hasOwnProperty('show_school_government_id')"
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ student.school_government_id }}
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <a :href="route('students.show', student.id)">
                                        {{ student.full_name }}
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import pickBy from 'lodash/pickBy';
import IndeterminateCheckboxSelect from '@/components/IndeterminateCheckboxSelect.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import SlideOver from '@/components/SlideOver.vue';
import AppButton from '@/components/AppButton.vue';

export default {
    components: {
        AppButton,
        SlideOver,
        PanelPage,
        IndeterminateCheckboxSelect,
        SubmitFormButton,
    },
    layout: Layout,
    name: 'Show',
    props: [
        'chartDataOptions',
        'average',
        'averagePercent',
        'filters',
        'groupsForCheckboxes',
        'groups',
        'tagsByCategory',
        'selectedTotal',
        'total',
    ],
    data() {
        return {
            form: this.filters,
            chartData: JSON.parse(JSON.stringify(this.chartDataOptions)),
            isLoading: false,
            slideOverOpen: false,
            lastGroupSelected: undefined,
            selectedFilters: [],
        };
    },
    computed: {
        filteredForm: function () {
            return pickBy(this.form);
        },
        students: function () {
            if (!this.filters.hasOwnProperty('list')) {
                return [];
            }

            return this.groups.flatMap((g) => {
                return g.students;
            });
        },
        tagGroups: function () {
            return Object.entries(this.tagsByCategory).map((c) => {
                const [key, value] = c;
                return {
                    category: key,
                    name: this.$t('messages.' + key),
                    tags: value,
                };
            });
        },
    },
    methods: {
        getParams() {
            return Object.keys(this.filteredForm).length ? this.filteredForm : {};
        },
        submit() {
            this.$forceUpdate();
            this.isLoading = true;

            const params = this.getParams();
            delete params.list;
            this.$inertia.get('/genie', params, {
                // preserveScroll: true,
                only: [
                    'chartDataOptions',
                    'average',
                    'averagePercent',
                    'filters',
                    'groupsForCheckboxes',
                    'groups',
                    'selectedTotal',
                    'total',
                ],
                onFinish: () => {
                    this.isLoading = false;
                },
            });
        },
        list() {
            this.$forceUpdate();
            this.isLoading = true;
            const params = this.getParams();
            params.list = true;
            this.$inertia.get('/genie', params, {
                // preserveScroll: true,
                only: [
                    'chartDataOptions',
                    'average',
                    'averagePercent',
                    'filters',
                    'groupsForCheckboxes',
                    'groups',
                    'selectedTotal',
                    'total',
                ],
                onFinish: () => {
                    this.isLoading = false;
                },
            });
        },
        clear() {
            this.isLoading = true;
            this.$inertia.get(
                '/genie',
                {},
                {
                    // preserveScroll: true,
                    onFinish: () => {
                        this.isLoading = false;
                    },
                },
            );
        },
        addCheckedSubset(add) {
            const groups = this.form.groups;
            if (add.length) {
                add.forEach((g) => {
                    if (!groups.includes(g.id)) {
                        groups.push(g.id);
                    }
                });
            }
            return groups;
        },
        removeCheckedSubset(remove) {
            const groups = this.form.groups;
            if (remove.length) {
                remove.forEach((g) => {
                    if (groups.includes(g.id)) {
                        const index = groups.indexOf(g.id);
                        groups.splice(index, 1);
                        // this.$delete(groups, groups.indexOf(g.id))
                    }
                });
            }

            return groups;
        },
        toggleTag(id) {
            if (this.form.tags.includes(id)) {
                this.form.tags.splice(this.form.tags.indexOf(id), 1);
            } else {
                this.form.tags.push(id);
            }
            this.$forceUpdate();
        },
        toggleGroup(event, index) {
            const savedIndex = this.lastGroupSelected;
            this.lastGroupSelected = index;

            // If this is the first pass or no shift key was
            // pressed, just bail.
            if (savedIndex === undefined || !event.shiftKey) return;

            // Shift-clicked the same group :-/
            if (index === savedIndex) return;

            // Get the subset of the message list between the
            // two indices.
            console.time('toggle range');
            const subset = this.groupsForCheckboxes.slice(Math.min(index, savedIndex), Math.max(index, savedIndex) + 1);

            // Determine the operation based on the checked state
            // of the clicked checkbox.
            const toggleFn = event.currentTarget.checked ? this.addCheckedSubset : this.removeCheckedSubset;

            this.form.groups = toggleFn(subset);
            console.timeEnd('toggle range');
            this.$forceUpdate();
        },
    },
    watch: {
        // form: {
        //     handler: function(newForm) {
        //         if (!newForm.hasOwnProperty('study_variable') || !newForm.study_variable) {
        //             this.form.denominator = null;
        //         }
        //         if (!newForm.hasOwnProperty('grade') || !newForm.grade) {
        //             this.form.denominator = null;
        //         }
        //         // this.$forceUpdate();
        //     },
        //     deep: true
        // }
    },
    created() {
        if (!this.form.hasOwnProperty('groups')) {
            // this.members [newMember.name] = newMember;
            // this.$set(this.members, newMember.name,   newMember);
            // this.form.groups = [];
            this.form['groups'] = [];
        } else {
            // this.form.groups = this.form.groups.map(g => +g);
            this.form['groups'] = this.form.groups.map((g) => +g);
        }
        if (!this.form.hasOwnProperty('interventions')) {
            // this.form.interventions = [];
            this.form['interventions'] = [];
        }

        if (!this.form.hasOwnProperty('tags')) {
            // this.form.tags = [];
            this.form['tags'] = [];
        } else {
            // this.form.tags = this.form.tags.map(g => +g);
            this.form['tags'] = this.form.tags.map((g) => +g);
        }
    },
    mounted() {},
};
</script>
